import { useState, useEffect, FormEvent } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import type {
  AdminCompanySummary, AdminCompanyDetail, AdminCompanyUser,
} from '@vedjegy/shared';

type Panel = 'view' | 'edit' | 'users';

export default function AdminCompaniesPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.roles.includes('super_admin') ?? false;

  const [companies, setCompanies]   = useState<AdminCompanySummary[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');

  // Selected company detail
  const [selected, setSelected]     = useState<AdminCompanyDetail | null>(null);
  const [companyUsers, setCompanyUsers] = useState<AdminCompanyUser[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [panel, setPanel]           = useState<Panel>('view');

  // Edit form
  const [editName, setEditName]         = useState('');
  const [editType, setEditType]         = useState('');
  const [editEmpCount, setEditEmpCount] = useState('');
  const [editCity, setEditCity]         = useState('');
  const [editStreet, setEditStreet]     = useState('');
  const [editZip, setEditZip]           = useState('');
  const [editWebsite, setEditWebsite]   = useState('');
  const [saving, setSaving]             = useState(false);
  const [saveMsg, setSaveMsg]           = useState('');
  const [renewalSaving, setRenewalSaving] = useState(false);

  // Invite new applicant company
  const [showNewInvite, setShowNewInvite]     = useState(false);
  const [newInviteEmail, setNewInviteEmail]   = useState('');
  const [newInviteTaxId, setNewInviteTaxId]   = useState('');
  const [newInviteSending, setNewInviteSending] = useState(false);
  const [newInviteError, setNewInviteError]   = useState('');
  const [newInviteLink, setNewInviteLink]     = useState('');
  const [newInviteCompany, setNewInviteCompany] = useState('');
  const [newInviteCopied, setNewInviteCopied] = useState(false);

  // User management (existing company)
  const [inviteEmail, setInviteEmail]   = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [userMsg, setUserMsg]           = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    api.adminCompanies.list()
      .then((d) => setCompanies(d.companies))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  function loadDetail(id: string) {
    setDetailLoading(true);
    setSaveMsg(''); setUserMsg(null);
    api.adminCompanies.get(id)
      .then((d) => {
        setSelected(d.company);
        setCompanyUsers(d.users);
        setEditName(d.company.name);
        setEditType(d.company.type);
        setEditEmpCount(d.company.employeeCount?.toString() ?? '');
        setEditCity(d.company.hqCity ?? '');
        setEditStreet(d.company.hqStreet ?? '');
        setEditZip(d.company.hqZip ?? '');
        setEditWebsite(d.company.websiteUrl ?? '');
        setPanel('view');
        setInviteEmail('');
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setDetailLoading(false));
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setSaving(true); setSaveMsg('');
    try {
      await api.adminCompanies.patch(selected.id, {
        name:          editName    || undefined,
        type:          editType    || undefined,
        employeeCount: editEmpCount !== '' ? parseInt(editEmpCount, 10) : undefined,
        hqCity:        editCity    || undefined,
        hqStreet:      editStreet  || undefined,
        hqZip:         editZip     || undefined,
        websiteUrl:    editWebsite || undefined,
      });
      setSaveMsg('Mentve.');
      const listData = await api.adminCompanies.list();
      setCompanies(listData.companies);
      setSelected((prev) => prev ? {
        ...prev,
        name: editName, type: editType,
        employeeCount: editEmpCount !== '' ? parseInt(editEmpCount, 10) : null,
        hqCity: editCity, hqStreet: editStreet, hqZip: editZip, websiteUrl: editWebsite,
      } : prev);
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : 'Hiba.');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleRenewal() {
    if (!selected) return;
    setRenewalSaving(true);
    const newValue = !selected.isRenewal;
    try {
      await api.adminCompanies.setRenewal(selected.id, newValue);
      setSelected((prev) => prev ? { ...prev, isRenewal: newValue } : prev);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Hiba.');
    } finally {
      setRenewalSaving(false);
    }
  }

  async function handleInviteNew(e: FormEvent) {
    e.preventDefault();
    setNewInviteError(''); setNewInviteLink(''); setNewInviteCompany('');
    setNewInviteSending(true);
    try {
      const res = await api.adminCompanies.inviteNew({
        email: newInviteEmail.trim(),
        taxId: newInviteTaxId.trim(),
      });
      setNewInviteLink(res.inviteLink);
      setNewInviteCompany(res.companyName);
      setNewInviteEmail(''); setNewInviteTaxId('');
      // Refresh company list — new company may have been pre-created
      const listData = await api.adminCompanies.list();
      setCompanies(listData.companies);
    } catch (err) {
      setNewInviteError(err instanceof Error ? err.message : 'Küldés sikertelen.');
    } finally {
      setNewInviteSending(false);
    }
  }

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    if (!selected || !inviteEmail) return;
    setInviteSending(true); setUserMsg(null);
    try {
      await api.adminCompanies.inviteUser(selected.id, inviteEmail);
      setUserMsg({ ok: true, text: `Meghívó elküldve: ${inviteEmail}` });
      setInviteEmail('');
    } catch (err) {
      setUserMsg({ ok: false, text: err instanceof Error ? err.message : 'Küldés sikertelen.' });
    } finally {
      setInviteSending(false);
    }
  }

  async function handleRemoveUser(userId: string) {
    if (!selected) return;
    setUserMsg(null);
    try {
      await api.adminCompanies.removeUser(selected.id, userId);
      setCompanyUsers((prev) => prev.filter((u) => u.userId !== userId));
      const listData = await api.adminCompanies.list();
      setCompanies(listData.companies);
    } catch (err) {
      setUserMsg({ ok: false, text: err instanceof Error ? err.message : 'Hiba.' });
    }
  }

  const filtered = companies.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.taxId.includes(search) ||
    (c.hqCity ?? '').toLowerCase().includes(search.toLowerCase()),
  );

  const TYPE_LABELS: Record<string, string> = {
    sole_trader: 'Egyéni vállalkozó',
    company:     'Gazdasági társaság',
  };

  return (
    <AdminLayout>
      <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

        {/* ── Left: company list ── */}
        <div style={{
          width: selected ? 360 : '100%', flexShrink: 0,
          borderRight: selected ? '1px solid var(--border)' : 'none',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          transition: 'width .2s',
        }}>
          <div style={{ padding: '28px 28px 16px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 6 }}>
              ADMIN
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--primary)' }}>
                Cégek
              </h1>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => { setShowNewInvite((v) => !v); setNewInviteError(''); setNewInviteLink(''); }}
              >
                {showNewInvite ? 'Mégsem' : '+ Pályázó meghívása'}
              </button>
            </div>

            {showNewInvite && (
              <div style={{ marginBottom: 14, padding: '16px', background: 'var(--muted)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: 'var(--primary)' }}>
                  Új pályázó meghívása
                </div>
                <form onSubmit={(e) => void handleInviteNew(e)} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    type="email" placeholder="E-mail cím" required
                    value={newInviteEmail} onChange={(e) => setNewInviteEmail(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                  <input
                    type="text" placeholder="Adószám (8 jegy)" required maxLength={8} pattern="\d{8}"
                    value={newInviteTaxId} onChange={(e) => setNewInviteTaxId(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  />
                  {newInviteError && (
                    <div style={{ fontSize: 12, color: 'var(--destructive)' }}>{newInviteError}</div>
                  )}
                  {newInviteLink ? (
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--success, #16a34a)', marginBottom: 6 }}>
                        ✓ Meghívó elküldve — {newInviteCompany}
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input
                          readOnly value={newInviteLink}
                          style={{ flex: 1, fontSize: 11, boxSizing: 'border-box' }}
                          onFocus={(e) => e.target.select()}
                        />
                        <button
                          type="button" className="btn btn-ghost btn-sm"
                          onClick={() => {
                            void navigator.clipboard.writeText(newInviteLink);
                            setNewInviteCopied(true);
                            setTimeout(() => setNewInviteCopied(false), 2000);
                          }}
                        >
                          {newInviteCopied ? '✓ Másolva' : 'Másolás'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="submit" className="btn btn-primary btn-sm" disabled={newInviteSending}>
                      {newInviteSending ? 'Küldés…' : 'Meghívó küldése'}
                    </button>
                  )}
                </form>
              </div>
            )}

            <input
              type="search" placeholder="Keresés név, adószám, város…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading && <div style={{ padding: '24px 28px', color: 'var(--muted-foreground)' }}>Betöltés…</div>}
            {error && <div style={{ padding: '24px 28px', color: 'var(--destructive)' }}>{error}</div>}
            {!loading && filtered.length === 0 && (
              <div style={{ padding: '24px 28px', color: 'var(--muted-foreground)' }}>Nincs találat.</div>
            )}
            {filtered.map((c) => {
              const isActive = selected?.id === c.id;
              return (
                <button key={c.id} onClick={() => loadDetail(c.id)} style={{
                  width: '100%', textAlign: 'left', padding: '14px 28px',
                  background: isActive ? 'var(--muted)' : 'transparent',
                  border: 'none', borderBottom: '1px solid var(--border)',
                  cursor: 'pointer', display: 'block',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary)', marginBottom: 2 }}>
                        {c.name}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
                        {c.taxId}{c.hqCity ? ` · ${c.hqCity}` : ''}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted-foreground)', flexShrink: 0, marginTop: 2 }}>
                      {c.userCount} felh.
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Right: detail panel ── */}
        {selected && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {detailLoading ? (
              <div style={{ padding: 32, color: 'var(--muted-foreground)' }}>Betöltés…</div>
            ) : (
              <>
                {/* Panel header */}
                <div style={{ padding: '24px 32px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>{selected.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
                        {selected.taxId} · {TYPE_LABELS[selected.type] ?? selected.type}
                      </div>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)}>✕</button>
                  </div>
                  {/* Tab bar */}
                  <div style={{ display: 'flex', gap: 0 }}>
                    {(['view', 'edit', 'users'] as Panel[]).map((p) => {
                      const labels: Record<Panel, string> = {
                        view:  'Adatok',
                        edit:  'Szerkesztés',
                        users: `Felhasználók (${companyUsers.length})`,
                      };
                      return (
                        <button key={p} onClick={() => setPanel(p)}
                          style={{
                            padding: '8px 18px', border: 'none', background: 'transparent', cursor: 'pointer',
                            fontSize: 13, fontWeight: 700,
                            color: panel === p ? 'var(--primary)' : 'var(--muted-foreground)',
                            borderBottom: panel === p ? '2px solid var(--primary)' : '2px solid transparent',
                            marginBottom: -1,
                          }}>
                          {labels[p]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>

                  {/* ── View tab ── */}
                  {panel === 'view' && (
                    <div style={{ maxWidth: 560 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 32px', marginBottom: 20 }}>
                        {[
                          ['Adószám',              selected.taxId],
                          ['Cégforma',             TYPE_LABELS[selected.type] ?? selected.type],
                          ['Alkalmazottak száma',   selected.employeeCount?.toString() ?? '—'],
                          ['Székhely város',        selected.hqCity   ?? '—'],
                          ['Utca',                  selected.hqStreet ?? '—'],
                          ['Irányítószám',           selected.hqZip    ?? '—'],
                          ['Weboldal',              selected.websiteUrl ?? '—'],
                        ].map(([label, value]) => (
                          <div key={label}>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted-foreground)', marginBottom: 3 }}>{label}</div>
                            <div style={{ fontSize: 14, color: 'var(--primary)', wordBreak: 'break-all' }}>{value}</div>
                          </div>
                        ))}
                      </div>

                      {/* Renewal toggle — visible to all, editable by super_admin only */}
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px 16px', background: 'var(--muted)',
                        borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                      }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>Megújító pályázó</div>
                          <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>
                            {selected.isRenewal ? 'Igen — megújítói pályázatot tölt ki' : 'Nem — új pályázatot tölt ki'}
                          </div>
                        </div>
                        {isSuperAdmin && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ minWidth: 100, fontWeight: 600 }}
                            disabled={renewalSaving}
                            onClick={() => void handleToggleRenewal()}
                          >
                            {renewalSaving ? '…' : selected.isRenewal ? 'Megújítóból normál' : 'Megújítóvá teszem'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── Edit tab ── */}
                  {panel === 'edit' && (
                    <form onSubmit={(e) => { void handleSave(e); }} style={{ maxWidth: 480 }}>
                      <div className="field">
                        <label>Cégnév</label>
                        <input value={editName} onChange={(e) => setEditName(e.target.value)} />
                      </div>
                      <div className="field">
                        <label>Cégforma</label>
                        <select value={editType} onChange={(e) => setEditType(e.target.value)}>
                          <option value="company">Gazdasági társaság</option>
                          <option value="sole_trader">Egyéni vállalkozó</option>
                        </select>
                      </div>
                      <div className="field">
                        <label>Alkalmazottak száma</label>
                        <input type="number" min="0" value={editEmpCount} onChange={(e) => setEditEmpCount(e.target.value)} />
                      </div>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <div className="field" style={{ flex: 2 }}>
                          <label>Székhely város</label>
                          <input value={editCity} onChange={(e) => setEditCity(e.target.value)} />
                        </div>
                        <div className="field" style={{ flex: 1 }}>
                          <label>Irányítószám</label>
                          <input value={editZip} onChange={(e) => setEditZip(e.target.value)} />
                        </div>
                      </div>
                      <div className="field">
                        <label>Utca, házszám</label>
                        <input value={editStreet} onChange={(e) => setEditStreet(e.target.value)} />
                      </div>
                      <div className="field">
                        <label>Weboldal</label>
                        <input type="url" value={editWebsite} onChange={(e) => setEditWebsite(e.target.value)} placeholder="https://" />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                          {saving ? 'Mentés…' : 'Mentés'}
                        </button>
                        {saveMsg && (
                          <span style={{ fontSize: 13, color: saveMsg === 'Mentve.' ? 'var(--secondary-foreground)' : 'var(--destructive)' }}>
                            {saveMsg}
                          </span>
                        )}
                      </div>
                    </form>
                  )}

                  {/* ── Users tab ── */}
                  {panel === 'users' && (
                    <div style={{ maxWidth: 560 }}>

                      {/* Current users */}
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--muted-foreground)', marginBottom: 10 }}>
                        Jelenlegi felhasználók
                      </div>
                      {companyUsers.length === 0 && (
                        <div style={{ fontSize: 14, color: 'var(--muted-foreground)', marginBottom: 20 }}>Nincs hozzárendelt felhasználó.</div>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 32 }}>
                        {companyUsers.map((u) => (
                          <div key={u.userId} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '10px 14px', background: 'var(--muted)', borderRadius: 10,
                          }}>
                            <div>
                              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--primary)' }}>{u.email}</span>
                              {u.isPrimary && (
                                <span className="badge" style={{ marginLeft: 8, background: 'rgba(78,192,124,.15)', color: '#064935', fontSize: 10 }}>
                                  elsődleges
                                </span>
                              )}
                            </div>
                            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--destructive)' }}
                              onClick={() => void handleRemoveUser(u.userId)}>
                              Eltávolít
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Invite new user */}
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: 'var(--muted-foreground)', marginBottom: 8 }}>
                        Munkatárs meghívása
                      </div>
                      <p style={{ fontSize: 13, color: 'var(--muted-foreground)', margin: '0 0 14px' }}>
                        A meghívott e-mail-ben kap egy linket, amelyen keresztül regisztrálhat és automatikusan csatlakozik ehhez a céghez.
                      </p>
                      <form onSubmit={(e) => { void handleInvite(e); }} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                        <input
                          type="email" required
                          value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="kolléga@ceg.hu"
                          style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 14, boxSizing: 'border-box' }}
                        />
                        <button type="submit" className="btn btn-primary" disabled={inviteSending}>
                          {inviteSending ? 'Küldés…' : 'Meghívó küldése'}
                        </button>
                      </form>
                      {userMsg && (
                        <div style={{
                          padding: '9px 14px', borderRadius: 6, fontSize: 13,
                          background: userMsg.ok ? '#f0fdf4' : '#fef2f2',
                          color: userMsg.ok ? '#166534' : '#dc2626',
                          border: `1px solid ${userMsg.ok ? '#bbf7d0' : '#fca5a5'}`,
                        }}>
                          {userMsg.text}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
